/* global VXConfig */
'use strict';

import Constants from '../Constants';
import Routes from '../../utils/Routes';
import { doFetch, getUrlParams, mergeDeep } from '../../utils/CommonUtils';
import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import Flux from '../Flux';
import UrlBuilder from '../../utils/UrlBuilder';

const CHANGE_EVENTS = {
	ActorData: 'ActorData',
	ActorDataNoResult: 'actorDataNoResult',
	ActorNote: 'actorNoteChange',
	ActorPictures: 'actorPicturesChange',
	ActorVideos: 'actorVideosChange',
	ActorVideoTags: 'actorVideoTagsChange',
	ActorTopCams: 'actorTopCamsChange',
	ActorCamShows: 'actorCamShowsChange',
	ActorPromotedVideo: 'actorPromotedVideoChange',
	ActorTopVideos: 'actorTopVideosChange',
	VideoRecommendationsByActorId: 'videoRecommendationsByActorIdChange',
	VideoRecommendationsByAlbumId: 'videoRecommendationsByAlbumIdChange',
	ActorChatRatings: 'actorChatRatingsChange',
	ProfilePage: 'profilePageChange',
	ActorInterviews: 'actorInterviews',
	ActorRecommendedActors: 'actorRecommendedActors',
	OnlineState: 'onlineState',
	ActorSeoContent: 'actorSeoContentChange',
};
const _actorData = {};
const _actorMapNameToId = {};
const _actorNotes = {};
const _actorPictures = {};
const _actorPromotedVideos = {};
const _actorTopCams = {};
const _actorTopCamsTotal = {};
const _actorCamShows = {};
const _actorTopVideos = {};
const _actorFilteredVideoData = {};
const _actorRecommendedActors = [];
const _videoRecommendationsByActorId = {};
const _videoRecommendationsByAlbumId = {};
let _actorInterviews = {};
const _preliminaryActorData = {};
const _actorChatRatings = {};
const _actorHeatMaps = {};
let _profilePage = null;
let _actorDataNoResult = false;
const _actorNotesLoading = {};
const _actorSeoContent = {};
const _actorDataInFlight = {};
const _actorPromotedVideoInFlight = {};
const _videoRecommendationsPromises = {
	// Note that number of asked videos is ignored here
	album: {
		sameActorIds: {}, // :: albumId -> Promise
		differentActorIds: {},
	},
	actor: {
		sameActorIds: {},
		differentActorIds: {},
	},
	/**
	 * @param {number} actorId
	 * @param {number} countFromSameActor
	 * @param {number} countFromDifferentActor
	 * @return {boolean}
	 */
	existsByActorId: function (actorId, countFromSameActor, countFromDifferentActor) {
		return (
			(countFromSameActor > 0 && countFromDifferentActor === 0 && !!this.actor.sameActorIds[actorId]) ||
			(countFromDifferentActor > 0 && countFromSameActor === 0 && !!this.actor.differentActorIds[actorId])
		);
	},

	/**
	 * @param {number} actorId
	 * @param {number} countFromSameActor
	 * @param {number} countFromDifferentActor
	 * @param {Promise} promise
	 */
	setByActorId: function (actorId, countFromSameActor, countFromDifferentActor, promise) {
		if (countFromSameActor > 0 && countFromDifferentActor === 0) {
			if (!this.actor.sameActorIds[actorId]) {
				this.actor.sameActorIds[actorId] = promise;

				promise.finally(() => {
					delete this.actor.sameActorIds[actorId];
				});
			}
			return;
		}

		if (countFromDifferentActor > 0 && countFromSameActor === 0 && !this.actor.differentActorIds[actorId]) {
			this.actor.differentActorIds[actorId] = promise;
			promise.finally(() => {
				delete this.actor.sameActorIds[actorId];
			});
		}
	},

	/**
	 * @param {number} albumId
	 * @param {number} countFromSameActor
	 * @param {number} countFromDifferentActor
	 * @return {boolean}
	 */
	existsByAlbumId: function (albumId, countFromSameActor, countFromDifferentActor) {
		return (
			(countFromSameActor > 0 && countFromDifferentActor === 0 && !!this.album.sameActorIds[albumId]) ||
			(countFromDifferentActor > 0 && countFromSameActor === 0 && !!this.album.differentActorIds[albumId])
		);
	},

	/**
	 * @param {number} albumId
	 * @param {number} countFromSameActor
	 * @param {number} countFromDifferentActor
	 * @param {Promise} promise
	 */
	setByAlbumId: function (albumId, countFromSameActor, countFromDifferentActor, promise) {
		if (countFromSameActor > 0 && countFromDifferentActor === 0) {
			if (!this.album.sameActorIds[albumId]) {
				this.album.sameActorIds[albumId] = promise;
				promise.finally(() => {
					delete this.album.sameActorIds[albumId];
				});
			}
			return;
		}

		if (countFromDifferentActor > 0 && countFromSameActor === 0 && !this.album.differentActorIds[albumId]) {
			this.album.differentActorIds[albumId] = promise;
			promise.finally(() => {
				delete this.album.differentActorIds[albumId];
			});
		}
	},
};

const ActorStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: CHANGE_EVENTS,

	getActorData: function (actorId) {
		let actorData = null;
		if (typeof _actorData[actorId] !== 'undefined' && _actorData[actorId] !== null) {
			actorData = _actorData[actorId];
		}

		return actorData;
	},

	getActorDataByName: function (actorName) {
		let actorId = null;

		if (actorName) {
			actorName = actorName.toLowerCase();
		}

		if (typeof _actorMapNameToId[actorName] !== 'undefined' && _actorMapNameToId[actorName] !== null) {
			actorId = _actorMapNameToId[actorName];
		}

		return this.getActorData(actorId);
	},

	getActorDataNoResult: function () {
		return _actorDataNoResult;
	},

	getActorIdByName: function (actorName) {
		if (!actorName) {
			return null;
		}

		actorName = actorName.toLowerCase();
		return typeof _actorMapNameToId[actorName] !== 'undefined' ? _actorMapNameToId[actorName] : null;
	},

	_getActorNote: function (actorId) {
		let actorNote = null;
		if (typeof _actorNotes[actorId] !== 'undefined' && _actorNotes[actorId] !== null) {
			actorNote = _actorNotes[actorId];
		}

		return actorNote;
	},

	/**
	 * @param {String|Number} actorId
	 * @param {String|Number} fsk
	 * @return {string}
	 */
	getCacheKey(actorId, fsk) {
		return actorId + ':' + fsk;
	},

	/**
	 * @param {Number} actorId
	 * @param {Number} limit
	 * @param {String|Number} fsk
	 */
	getActorPictures: function (actorId, limit = -1, fsk = 'none') {
		let actorPictures = null;
		const key = this.getCacheKey(actorId, fsk);

		if (typeof _actorPictures[key] !== 'undefined' && _actorPictures[key] !== null) {
			actorPictures = _actorPictures[key];
		}

		if (null !== actorPictures && limit > 0 && limit < actorPictures.length) {
			actorPictures = actorPictures.slice(0, limit);
		}

		return actorPictures;
	},

	getActorTopVideosByName: function (actorName) {
		let actorVideos = null;
		let actorId = null;

		if (actorName) {
			actorName = actorName.toLowerCase();
		}

		if (typeof _actorMapNameToId[actorName] !== 'undefined' && _actorMapNameToId[actorName] !== null) {
			actorId = _actorMapNameToId[actorName];
		}

		if (typeof _actorTopVideos[actorId] !== 'undefined' && _actorTopVideos[actorId] !== null) {
			actorVideos = _actorTopVideos[actorId];
		}

		return actorVideos;
	},

	getActorTopCams: function (collectionName) {
		return _actorTopCams[collectionName] || null;
	},

	getActorTopCamsTotal: function (collectionName) {
		return _actorTopCamsTotal[collectionName] || null;
	},

	getActorCamShows: function (actorId) {
		return _actorCamShows[actorId] || null;
	},

	getVideoRecommendationsByActorId(actorId, sameActor) {
		return (_videoRecommendationsByActorId[actorId] && _videoRecommendationsByActorId[actorId][sameActor]) || null;
	},

	getVideoRecommendationsByAlbumId(albumId, sameActor) {
		return (_videoRecommendationsByAlbumId[albumId] && _videoRecommendationsByAlbumId[albumId][sameActor]) || null;
	},

	/**
	 * @param {String} actorName
	 * @param {Number} limit
	 * @param {String|Number} fsk
	 */
	getActorPicturesByName: function (actorName, limit = -1, fsk = 'none') {
		let actorId = null;

		if (actorName) {
			actorName = actorName.toLowerCase();
		}

		if (typeof _actorMapNameToId[actorName] !== 'undefined' && _actorMapNameToId[actorName] !== null) {
			actorId = _actorMapNameToId[actorName];
		}

		return this.getActorPictures(actorId, limit, fsk);
	},

	getActorPromotedVideo: function (actorId) {
		let actorVideo = null;
		if (typeof _actorPromotedVideos[actorId] !== 'undefined' && _actorPromotedVideos[actorId] !== null) {
			actorVideo = _actorPromotedVideos[actorId];
		}

		return actorVideo;
	},

	getPreliminaryActorData: function (actorName) {
		let actorData = null;

		if (actorName) {
			actorName = actorName.toLowerCase();
		}

		if (typeof _preliminaryActorData[actorName] !== 'undefined' && _preliminaryActorData[actorName] !== null) {
			actorData = _preliminaryActorData[actorName];
		}

		return actorData;
	},

	/**
	 * @param {number} actorId
	 * @param {string} ratingType
	 * @returns {Object|null}
	 */
	getActorChatRatings: function (actorId, ratingType) {
		const chatRatings = _actorChatRatings[actorId] || {};
		return chatRatings[ratingType] || null;
	},

	/**
	 * @param {number} actorId
	 * @returns {Object|null}
	 */
	getActorHeatMaps: function (actorId) {
		return _actorHeatMaps[actorId] || null;
	},

	/**
	 * @param {Number} actorId
	 * @param {string} ratingType
	 * @return {boolean}
	 */
	shouldShowRating: function (actorId, ratingType) {
		// no data at all
		if (!Object.hasOwn(_actorChatRatings, actorId)) {
			return false;
		}

		// not enough data
		const chatRatings = _actorChatRatings[actorId] || {};
		return (
			Object.hasOwn(chatRatings, ratingType) &&
			Object.hasOwn(chatRatings[ratingType], 'countRatings') &&
			chatRatings[ratingType].countRatings >= 10
		);
	},

	/**
	 * @param {Number} actorId
	 * @param {Number} first
	 * @return {Array}
	 */
	getModelRecommendations: function (actorId, first = 4) {
		if (!Object.hasOwn(_actorRecommendedActors, actorId)) {
			return null;
		}

		return _actorRecommendedActors[actorId].slice(0, first);
	},

	/**
	 * @param {int} actorId
	 * @param {boolean} state
	 *
	 * @return {boolean}
	 */
	setActorPromotedVideoState: function (actorId, state) {
		if (typeof _actorPromotedVideos[actorId] !== 'undefined' && _actorPromotedVideos[actorId] !== null) {
			_actorPromotedVideos[actorId].isFavoriteAlbum = state;
			return true;
		}
		return false;
	},

	storeActorNote: function (actorId, comment) {
		if (typeof _actorNotes[actorId] !== 'undefined' && _actorNotes[actorId] !== null) {
			_actorNotes[actorId].comment = comment;
		}

		doFetch(
			Routes.getRoute(Routes.Names.ACTOR_NOTE_STORE),
			{
				actorId: actorId,
				comment: comment,
			},
			Constants.HttpMethods.POST,
			true,
		).then((result) => {
			processResult(result);
		});
	},

	/**
	 * @param {Number} actorId
	 * @param {Array} heatMaps
	 * @param {Number|Null} hoursToBeOnline
	 * @param {string} actorName
	 * @param {String|null} memo
	 * @param {Array} errors
	 */
	storeActorHeatMaps: function (actorId, heatMaps, hoursToBeOnline, actorName, memo, errors) {
		_actorHeatMaps[actorId] = { heatMaps, hoursToBeOnline, actorName, memo, errors };
	},

	getProfilePage: function () {
		return _profilePage;
	},

	emitActorOnlineStateChange: function (actorId, isOnline) {
		this.emit(CHANGE_EVENTS.OnlineState, actorId, isOnline);
	},

	/**
	 * @param {Number} actorId
	 * @param {String} imageSize
	 * @param {Boolean} useFallback
	 * @param {String} durationFormat
	 * @return {null|*}
	 */
	getActorInterviews: function (
		actorId,
		imageSize = Constants.Interviews.IMAGE_SIZE_720,
		useFallback = true,
		durationFormat = Constants.Interviews.DURATION_SEC,
	) {
		const fallbackKey = useFallback ? 1 : 0;
		const exists =
			Object.hasOwn(_actorInterviews, actorId) &&
			Object.hasOwn(_actorInterviews[actorId], imageSize) &&
			Object.hasOwn(_actorInterviews[actorId][imageSize], fallbackKey) &&
			Object.hasOwn(_actorInterviews[actorId][imageSize][fallbackKey], durationFormat);

		return exists ? _actorInterviews[actorId][imageSize][fallbackKey][durationFormat] : [];
	},

	getActorSeoContent: function (actorId, tagGroup) {
		if (_actorSeoContent[actorId] && _actorSeoContent[actorId][tagGroup]) {
			return _actorSeoContent[actorId][tagGroup];
		}
	},

	clearCache: function () {
		// remove all keys from cache objects
		[
			_actorData,
			_actorMapNameToId,
			_actorPictures,
			_actorPromotedVideos,
			_actorTopVideos,
			_actorFilteredVideoData,
			_videoRecommendationsByActorId,
			_videoRecommendationsByAlbumId,
		].forEach((obj) => {
			for (const key in obj) {
				delete obj[key];
			}
		});
	},
	loadActorCamShows(actorId) {
		Flux.Actor.getProfileLiveShowTicket(actorId, (res) => {
			if (res) {
				_actorCamShows[actorId] = res;
				ActorStore.emit(CHANGE_EVENTS.ActorCamShows, actorId);
			}
		});
	},
	loadActorTopCams(args) {
		const collectionName = args.collectionNames[0];
		Flux.Vxql.getCollectionsV2({ ...args }, (res) => {
			if (res.data.collections_v2.length > 0) {
				const items = res.data.collections_v2[0].models.items;
				// Load-more pages extend the cached list so a revisit restores everything already loaded.
				_actorTopCams[collectionName] = args.offsetModels > 0 ? [...(_actorTopCams[collectionName] || []), ...items] : items;
				_actorTopCamsTotal[collectionName] = res.data.collections_v2[0].models.total;
				ActorStore.emit(CHANGE_EVENTS.ActorTopCams, collectionName);
			}
		});
	},
	loadActorSeoContent(actorId, tagGroup) {
		if (_actorSeoContent[actorId] && _actorSeoContent[actorId][tagGroup]) {
			return;
		}

		doFetch(
			Routes.getRoute(Routes.Names.ACTOR_SEO_CONTENT, {
				actorId: actorId,
				tagGroup: encodeURIComponent(tagGroup),
			}),
			null,
			Constants.HttpMethods.GET,
			true,
		).then((result) => {
			if (!_actorSeoContent[actorId]) {
				_actorSeoContent[actorId] = {};
			}
			_actorSeoContent[actorId][tagGroup] = result;

			ActorStore.emit(CHANGE_EVENTS.ActorSeoContent, actorId);
		});
	},
	loadActorTopVideos(actorIdOrName) {
		if (actorIdOrName && typeof actorIdOrName === 'string') {
			actorIdOrName = actorIdOrName.toLowerCase();
		}

		if (typeof _actorTopVideos[actorIdOrName] !== 'undefined') {
			_actorTopVideos[actorIdOrName] = null;
		}

		doFetch(Routes.getRoute(Routes.Names.GALLERY_GET_TOP_VIDEOS, { actorIdOrName: actorIdOrName }), null, Constants.HttpMethods.GET, true).then(
			(result) => {
				processResult(result);
			},
		);
	},

	loadActorPromotedVideo(actorId) {
		if (_actorPromotedVideoInFlight[actorId]) return;

		_actorPromotedVideos[actorId] = null;
		_actorPromotedVideoInFlight[actorId] = true;
		doFetch(Routes.getRoute(Routes.Names.GALLERY_GET_PROMOTED_VIDEO, { actorId: actorId }), null, Constants.HttpMethods.GET, true).then(
			(result) => {
				_actorPromotedVideoInFlight[actorId] = false;
				processResult(result);
			},
		);
	},

	loadActorState(actorId) {
		doFetch(Routes.getRoute(Routes.Names.ACTOR_STATE_BY_ID, { actorId: actorId }), null, Constants.HttpMethods.GET, true).then((result) => {
			processResult(result);
		});
	},

	loadActorNote(actorId) {
		if (_actorNotesLoading[actorId]) {
			return;
		}

		_actorNotes[actorId] = null;
		_actorNotesLoading[actorId] = true;
		doFetch(Routes.getRoute(Routes.Names.ACTOR_NOTE_BY_ID, { actorId: actorId }), null, Constants.HttpMethods.GET, true).then((result) => {
			processResult(result);
			_actorNotesLoading[actorId] = false;
		});
	},

	setPreliminaryData(actorData) {
		if (actorData) {
			const actorName = actorData.name.toLowerCase();

			_preliminaryActorData[actorName] = actorData;
		}
	},

	setProfilePage(pageId) {
		if (pageId !== _profilePage) {
			_profilePage = pageId;
			ActorStore.emit(CHANGE_EVENTS.ProfilePage);
		}
	},
	loadVideoRecommendationsByActorId(actorId, countFromSameActor, countFromDifferentActor) {
		if (_videoRecommendationsPromises.existsByActorId(actorId, countFromSameActor, countFromDifferentActor)) {
			return;
		}
		const promise = Flux.Vxql.getVideoRecommendationsByActorId(actorId, countFromSameActor, countFromDifferentActor);
		_videoRecommendationsPromises.setByActorId(actorId, countFromSameActor, countFromDifferentActor, promise);
		promise.then(({ data }) => {
			const recommendations = data.model.recommendations;

			if (recommendations) {
				if (countFromSameActor > 0) {
					storeVideoRecommendations(recommendations, actorId, true, _videoRecommendationsByActorId);
				}
				if (countFromDifferentActor > 0) {
					storeVideoRecommendations(recommendations, actorId, false, _videoRecommendationsByActorId);
				}

				// Note: lacks sameActor param
				ActorStore.emit(CHANGE_EVENTS.VideoRecommendationsByActorId, actorId);
			}
		});
	},

	loadVideoRecommendationsByAlbumId(albumId, countFromSameActor, countFromDifferentActor) {
		if (_videoRecommendationsPromises.existsByAlbumId(albumId, countFromSameActor, countFromDifferentActor)) {
			return;
		}
		const promise = Flux.Vxql.getVideoRecommendationsByAlbumId(albumId, countFromSameActor, countFromDifferentActor);
		_videoRecommendationsPromises.setByAlbumId(albumId, countFromSameActor, countFromDifferentActor, promise);
		promise.then(({ data }) => {
			const recommendations = data.video.recommendations;

			if (recommendations) {
				if (countFromSameActor > 0) {
					storeVideoRecommendations(recommendations, albumId, true, _videoRecommendationsByAlbumId);
				}
				if (countFromDifferentActor > 0) {
					storeVideoRecommendations(recommendations, albumId, false, _videoRecommendationsByAlbumId);
				}

				// Note: lacks sameActor param
				ActorStore.emit(CHANGE_EVENTS.VideoRecommendationsByAlbumId, albumId);
			}
		});
	},

	cacheActorChatRatings(chatRatings, actorId) {
		if (typeof _actorChatRatings[actorId] === 'undefined') {
			_actorChatRatings[actorId] = {};
		}

		if (chatRatings && chatRatings.lastNinetyDays) {
			_actorChatRatings[actorId] = { ..._actorChatRatings[actorId], ...chatRatings };
			ActorStore.emit(CHANGE_EVENTS.ActorChatRatings, actorId);
		}
	},

	saveInterviewsInCache(interviews, actorId, imageSize, useFallback = true, durationFormat = Constants.Interviews.DURATION_SEC) {
		_actorInterviews = mergeDeep({}, _actorInterviews, {
			[actorId]: {
				[imageSize]: {
					[useFallback ? 1 : 0]: {
						[durationFormat]: interviews,
					},
				},
			},
		});
		interviews.length > 0 && ActorStore.emit(CHANGE_EVENTS.ActorInterviews, actorId);
	},

	cacheActorRecommendedActors(actorId, models) {
		if (!Object.hasOwn(_actorRecommendedActors, actorId)) {
			_actorRecommendedActors[actorId] = [];
		}

		if (Array.isArray(models)) {
			_actorRecommendedActors[actorId] = models;
			ActorStore.emit(CHANGE_EVENTS.ActorRecommendedActors, actorId);
		}
	},
	loadActorPictures(actorIdOrName, limit = -1, fsk = 'none') {
		if (actorIdOrName && typeof actorIdOrName === 'string') {
			actorIdOrName = actorIdOrName.toLowerCase();
		}

		if (typeof _actorPictures[actorIdOrName] !== 'undefined') {
			_actorPictures[actorIdOrName] = null;
		}

		const params = {
			actorIdOrName: actorIdOrName,
			payload: JSON.stringify({
				limit: limit,
				fsk: fsk,
			}),
		};

		doFetch(Routes.getRoute(Routes.Names.GALLERY_GET_SEDCARDS_BY_FSK, params), null, Constants.HttpMethods.GET, true).then((result) => {
			processResult(result);
		});
	},
	loadActorData(actorIdOrName) {
		if (actorIdOrName && typeof actorIdOrName === 'string') {
			actorIdOrName = actorIdOrName.toLowerCase();
		}

		if (_actorDataInFlight[actorIdOrName]) return;

		if (typeof _actorData[actorIdOrName] !== 'undefined') {
			_actorData[actorIdOrName] = null;
		}

		let payload = null;
		const urlParams = getUrlParams(window.location.href);
		if (typeof urlParams.qmday !== 'undefined') {
			payload = { qmday: urlParams.qmday };
		}

		_actorDataInFlight[actorIdOrName] = true;
		doFetch(
			Routes.getRoute(Routes.Names.ACTOR_DATA_BY_ID_OR_NAME, { actorIdOrName: actorIdOrName }),
			payload,
			Constants.HttpMethods.GET,
			true,
		).then((result) => {
			_actorDataInFlight[actorIdOrName] = false;
			processResult(result, actorIdOrName);
		});
	},
	loadActorDataByName(actorName) {
		let actorIdOrName = actorName;

		if (actorIdOrName && typeof actorIdOrName === 'string') {
			actorIdOrName = actorIdOrName.toLowerCase();
		}

		if (typeof _actorMapNameToId[actorName] !== 'undefined' && _actorMapNameToId[actorName] !== null) {
			actorIdOrName = _actorMapNameToId[actorName];
		}

		this.loadActorData(actorIdOrName);
	},
	setIsVoyeurInSingleChatAllowed(actorId, isAvailable) {
		if (typeof _actorData[actorId] !== 'undefined' && _actorData[actorId] !== null) {
			_actorData[actorId].isVoyeurInSingleChatAllowed = isAvailable;
			ActorStore.emit(CHANGE_EVENTS.ActorData, actorId);
		}
	},
});

function getButtonConfigByActorData(actorData) {
	return {
		actorId: actorData.id,
		actorLink: UrlBuilder.buildActorProfileUrl(actorData.name),
		isActorOnline: actorData.isOnline,
		freeChat: actorData.freeChat,
		isBannedCountryCode: actorData.bannedCountryCodes.includes(VXConfig.guestCountryCode),
		isActorAvailableForConversionChat: actorData.isActorAvailableForConversionChat,
		isActorNotAvailableForConversionChat: !actorData.isActorAvailableForConversionChat,
		isOnlineMobileVideocall: actorData.isOnlineMobileVideocall,
		isCurrentlyTicketShow: actorData.isCurrentlyTicketShow,
		prices: actorData.prices,
		voucherChatMinutes: actorData.prices?.voucherChatMinutes,
		isLivePreviewEnabled: actorData.isLivePreviewEnabled,
		isVoyeurChatAvailable: actorData.isVoyeurChatAvailable,
		guestHasTicket: actorData.guestHasTicket,
		multiChatPrice: actorData.prices?.video || 0,
		singleC2CChatPrice: actorData.prices?.singlec2c || 0,
	};
}

function processResult(result, actorIdOrName) {
	let changeEvent = null;
	let changeEventArg;
	if (result.actorData) {
		const actorName = result.actorData.name.toLowerCase();
		_actorMapNameToId[actorName] = result.actorData.id;
		_actorData[result.actorData.id] = result.actorData;
		_actorDataNoResult = false;
		Flux.Button.setButtonConfig([getButtonConfigByActorData(result.actorData)]);
		changeEvent = CHANGE_EVENTS.ActorData;
	} else if (result.data && result.data.actor) {
		const actorName = result.data.actor.name.toLowerCase();
		_actorMapNameToId[actorName] = result.data.actor.id;
	} else {
		_actorDataNoResult = true;
		changeEvent = CHANGE_EVENTS.ActorDataNoResult;
		// Scope the no-result signal to the actor that was actually being fetched, so a stale
		// response for an actor the user has since navigated away from can't flip this flag
		// for a different, still-loading actor (see ActorProfile.jsx's getDerivedStateFromProps).
		changeEventArg = actorIdOrName;
	}

	if (result.actorState && _actorData[result.actorState.id]) {
		let s;
		for (s in result.actorState) {
			_actorData[result.actorState.id][s] = result.actorState[s];
		}
		changeEvent = CHANGE_EVENTS.ActorData;
	}

	if (result.data) {
		if (typeof result.data.photos !== 'undefined') {
			const key = ActorStore.getCacheKey(result.data.actor?.id, result.data.fsk || 'none');
			_actorPictures[key] = result.data;
			changeEvent = CHANGE_EVENTS.ActorPictures;
		} else if (typeof result.data.promotedVideo !== 'undefined') {
			_actorPromotedVideos[result.data.actor.id] = result.data;
			changeEvent = CHANGE_EVENTS.ActorPromotedVideo;
		} else if (typeof result.data.topVideos !== 'undefined') {
			_actorTopVideos[result.data.actor.id] = result.data.topVideos;
			changeEvent = CHANGE_EVENTS.ActorTopVideos;
		}
	}

	if (typeof result.actorNote !== 'undefined') {
		_actorNotes[result.actorNote.actorId] = result.actorNote;
		changeEvent = CHANGE_EVENTS.ActorNote;
	}

	if (changeEvent) {
		ActorStore.emit(changeEvent, changeEventArg);
	}
}

function storeVideoRecommendations(recommendationsObject, id, sameActor, stateObject) {
	const data = recommendationsObject[sameActor ? 'fromSameActor' : 'fromDifferentActor'];

	if (data) {
		let recommendationsForId = stateObject[id];

		if (!recommendationsForId) {
			recommendationsForId = {};
			stateObject[id] = recommendationsForId;
		}

		recommendationsForId[sameActor] = data;
	}
}

export default ActorStore;
